import {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {PorQue} from '@/components/ui/por-que'
import {FichaDatos, type BloqueFicha} from '@/components/ui/ficha-datos'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FixedStrip} from '@/components/fixed-strip'
import {PageHeading} from '@/components/page-heading'
import {SectionCard} from '@/components/section-card'
import {ConsentStep, SealedNotice} from '@/components/perfil-sociodemografico-consent'
import {LaboralStepFields} from '@/components/perfil-sociodemografico-laboral-step'
import {PersonalStepFields} from '@/components/perfil-sociodemografico-personal-step'
import {SaludStepFields} from '@/components/perfil-sociodemografico-salud-step'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {
  draftFromPerfil,
  useMyPerfilSociodemografico,
  useMyPerfilSociodemograficoHistorial,
  useMyPerfilSociodemograficoVersion,
  useSealMyPerfilSociodemografico,
  useUpdateMyPerfilSociodemografico,
  type Draft,
  type PerfilSociodemografico as PerfilRead,
  type PerfilSociodemograficoUpdate,
} from '@/hooks/empresa/mi_espacio/use-perfil-sociodemografico'
import {translateApiError} from '@/lib/errors'
import {localToday} from '@/lib/risk'
import {cn} from '@/lib/utils'

const TOTAL_STEPS = 3

const STEP_KEY: Record<number, {title: string; porque: string; porqueLabel: string}> = {
  1: {
    title: 'perfilSociodemografico.pasos.laboral',
    porque: 'perfilSociodemografico.laboral.porque',
    porqueLabel: 'perfilSociodemografico.porqueLabel',
  },
  2: {
    title: 'perfilSociodemografico.pasos.personal',
    porque: 'perfilSociodemografico.personal.porque',
    porqueLabel: 'perfilSociodemografico.porqueLabel',
  },
  3: {
    title: 'perfilSociodemografico.pasos.salud',
    porque: 'perfilSociodemografico.salud.porque',
    porqueLabel: 'perfilSociodemografico.salud.porqueLabel',
  },
}

/** Solo con el perfil sellado: la versión siguiente nace de una re-solicitud del dueño. */
function HistorialSection({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data: versions} = useMyPerfilSociodemograficoHistorial(businessId, page)
  const [openVersionId, setOpenVersionId] = useState<string | null>(null)
  const {data: openVersion} = useMyPerfilSociodemograficoVersion(businessId, openVersionId ?? undefined)

  if (!versions || versions.total <= 1) return null

  return (
    <SectionCard title={t('perfilSociodemografico.historial.title')} testId="perfil-sociodemografico-historial">
      <ul className="space-y-2">
        {versions.items.map((version) => (
          <li key={version.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="font-mono text-xs text-muted-foreground">
              {version.sealed_at
                ? t('perfilSociodemografico.historial.sealedOn', {date: version.sealed_at.slice(0, 10)})
                : t('perfilSociodemografico.historial.notSealed')}
              {version.vigente && ` · ${t('perfilSociodemografico.historial.vigente')}`}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setOpenVersionId(version.id)}
              data-testid={`perfil-sociodemografico-historial-open-${version.id}`}>
              {t('perfilSociodemografico.historial.view')}
            </Button>
          </li>
        ))}
      </ul>
      <PaginationBar page={versions.page} pageSize={versions.page_size} total={versions.total} onPageChange={setPage} />
      {openVersion && (
        <div className="surface-divider mt-4 border-t pt-4" data-testid="perfil-sociodemografico-historial-detail">
          <SealedSummary perfil={openVersion} />
        </div>
      )}
    </SectionCard>
  )
}

/** Sin consentimiento el servidor rechaza cualquier PUT (409); sellar congela la versión vigente. */
export function MiPerfilSociodemograficoBody({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const {data: perfil, isLoading} = useMyPerfilSociodemografico(businessId)
  const update = useUpdateMyPerfilSociodemografico(businessId)
  const seal = useSealMyPerfilSociodemografico(businessId)

  const [draft, setDraft] = useState<Draft | null>(null)
  const [draftPerfilId, setDraftPerfilId] = useState<string | null>(null)
  const [step, setStep] = useState(1)
  const [confirmSealOpen, setConfirmSealOpen] = useState(false)
  const [sealError, setSealError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (!perfil || draftPerfilId === (perfil.id ?? null)) return
    setDraft(draftFromPerfil(perfil))
    setDraftPerfilId(perfil.id ?? null)
    setStep(Math.min(Math.max(perfil.form_step_reached, 1), TOTAL_STEPS))
  }, [perfil, draftPerfilId])

  if (isLoading || !perfil) return <Skeleton className="h-72 w-full" />

  const consented = perfil.consent_at !== null
  const sealed = perfil.sealed_at !== null

  function updateField(field: keyof Draft, value: string) {
    setDraft((d) => (d ? {...d, [field]: value} : d))
  }

  /** `translateApiError` se llama por su efecto: marca el 422 como comunicado y la red global de
   * `red-errores-mutacion.ts` se aparta, así este mensaje es el único que se ve. */
  function commit(fields: Partial<PerfilSociodemograficoUpdate>) {
    if (godReadOnly) return
    update.mutate(fields, {
      onSuccess: () => setSaveError(null),
      onError: (err) => {
        if (!isAxiosError(err) || err.response?.status !== 422) return
        translateApiError(err)
        setSaveError(t('perfilSociodemografico.saveError'))
      },
    })
  }

  // Sube `step_reached` aunque cada respuesta ya se guardó sola, para que recargar vuelva acá.
  function goToStep(next: number) {
    setStep(next)
    commit({step_reached: next})
  }

  if (!draft) return <Skeleton className="h-72 w-full" />

  return (
    <div className="space-y-4" data-testid="mi-perfil-sociodemografico">
      {!consented ? (
        // El consentimiento (Ley 1581) es una guarda legal: flujo y copy se conservan tal cual.
        <SectionCard title={t('perfilSociodemografico.pasos.consentimiento')}>
          <ConsentStep businessId={businessId} onConsented={() => setStep(1)} />
        </SectionCard>
      ) : sealed ? (
        <div className="space-y-4" data-testid="perfil-sociodemografico-sealed">
          <SectionCard title={t('perfilSociodemografico.pasos.sellado')}>
            <SealedNotice perfil={perfil} />
            <div className="mt-4">
              <SealedSummary perfil={perfil} />
            </div>
          </SectionCard>
          <HistorialSection businessId={businessId} />
        </div>
      ) : (
        <>
          <FixedStrip className="z-10 bg-background/95 backdrop-blur-sm">
            <div className="mx-auto flex max-w-2xl items-center justify-between">
              <p
                className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase"
                data-testid="perfil-sociodemografico-step-indicator">
                {t('perfilSociodemografico.stepIndicator', {step, total: TOTAL_STEPS})}
              </p>
              <div className="flex gap-1" aria-hidden="true">
                {Array.from({length: TOTAL_STEPS}, (_, i) => i + 1).map((s) => (
                  <span key={s} className={cn('h-1.5 w-6 rounded-full', s <= step ? 'bg-primary' : 'bg-secondary')} />
                ))}
              </div>
            </div>
          </FixedStrip>

          <SectionCard
            title={t(STEP_KEY[step].title)}
            actions={
              <PorQue
                label={t(STEP_KEY[step].porqueLabel)}
                testId="perfil-sociodemografico-porque"
                contenidoTestId="perfil-sociodemografico-porque-content">
                <p>{t(STEP_KEY[step].porque)}</p>
              </PorQue>
            }>
            {saveError && (
              <p className="mb-4 text-sm text-destructive" data-testid="perfil-sociodemografico-save-error">
                {saveError}
              </p>
            )}

            <div>
              {step === 1 && (
                <LaboralStepFields
                  draft={draft}
                  perfil={perfil}
                  godReadOnly={godReadOnly}
                  maxFechaIngreso={localToday()}
                  updateField={updateField}
                  commit={commit}
                />
              )}

              {step === 2 && (
                <PersonalStepFields draft={draft} godReadOnly={godReadOnly} updateField={updateField} commit={commit} />
              )}

              {step === 3 && (
                <SaludStepFields
                  draft={draft}
                  perfil={perfil}
                  godReadOnly={godReadOnly}
                  updateField={updateField}
                  commit={commit}
                />
              )}
            </div>

            <div className="surface-divider -mx-5 -mb-5 mt-5 flex items-center justify-between gap-3 border-t px-5 py-3.5">
              <Button type="button" variant="outline" size="sm" disabled={step <= 1} onClick={() => setStep(step - 1)}>
                {t('common.back')}
              </Button>
              {step < TOTAL_STEPS ? (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => goToStep(step + 1)}
                  data-testid="perfil-sociodemografico-next">
                  {t('perfilSociodemografico.next')}
                </Button>
              ) : (
                // Con un auto-guardado en vuelo, abrir el sello es correr contra él (KAI-145, PYTHON-3P).
                <Button
                  type="button"
                  size="sm"
                  disabled={godReadOnly || update.isPending || seal.isPending}
                  onClick={() => setConfirmSealOpen(true)}
                  data-testid="perfil-sociodemografico-seal-open">
                  {t('perfilSociodemografico.seal.action')}
                </Button>
              )}
            </div>
          </SectionCard>

          <ConfirmDialog
            open={confirmSealOpen}
            onOpenChange={(open) => {
              setConfirmSealOpen(open)
              if (open) setSealError(null)
            }}
            title={t('perfilSociodemografico.seal.confirmTitle')}
            description={t('perfilSociodemografico.seal.confirmBody')}
            confirmLabel={t('perfilSociodemografico.seal.confirmAction')}
            cancelLabel={t('common.cancel')}
            pending={seal.isPending}
            porque={{
              label: t('perfilSociodemografico.seal.porqueLabel'),
              contenido: <p>{t('perfilSociodemografico.seal.porqueBody')}</p>,
              testId: 'perfil-sociodemografico-seal-porque',
              contenidoTestId: 'perfil-sociodemografico-seal-porque-content',
            }}
            onConfirm={() => {
              if (godReadOnly) return
              seal.mutate(undefined, {
                onSuccess: () => setConfirmSealOpen(false),
                onError: (err) => setSealError(translateApiError(err)),
              })
            }}
          />
          {sealError && confirmSealOpen && (
            <p className="text-sm text-destructive" data-testid="perfil-sociodemografico-seal-error">
              {sealError}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export function MiPerfilSociodemograficoPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business} = useBusiness(businessId)

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeading title={t('perfilSociodemografico.title')} subtitle={business?.name} />
      <MiPerfilSociodemograficoBody businessId={businessId!} />
    </div>
  )
}

const VACIO = '—'

/** «Salud y emergencia» es de otra custodia: solo se ve en la pantalla del propio trabajador. */
function SealedSummary({perfil}: {perfil: PerfilRead}) {
  const {t} = useTranslation()
  const opcion = (prefijo: string, valor: string | null | undefined) => (valor ? t(`${prefijo}.${valor}`) : VACIO)
  const contacto = perfil.contacto_emergencia_nombre
  const bloques: BloqueFicha[] = [
    {
      clave: 'vinculo',
      titulo: t('perfilSociodemografico.bloques.vinculo'),
      testId: 'perfil-sociodemografico-bloque-vinculo',
      filas: [
        {
          clave: 'tipo_contrato',
          etiqueta: t('perfilSociodemografico.tipoContrato'),
          valor: opcion('perfilSociodemografico.tipoContrato', perfil.tipo_contrato),
        },
        {
          clave: 'modalidad',
          etiqueta: t('perfilSociodemografico.modalidad'),
          valor: opcion('perfilSociodemografico.modalidad', perfil.modalidad),
        },
        {
          clave: 'antiguedad',
          etiqueta: t('perfilSociodemografico.antiguedad'),
          valor:
            perfil.antiguedad_anios != null
              ? t('perfilSociodemografico.antiguedadValue', {count: perfil.antiguedad_anios})
              : VACIO,
        },
        {
          clave: 'edad',
          etiqueta: t('perfilSociodemografico.edad'),
          valor: perfil.edad != null ? t('perfilSociodemografico.edadValue', {count: perfil.edad}) : VACIO,
        },
      ],
    },
    {
      clave: 'salud',
      titulo: t('perfilSociodemografico.bloques.saludEmergencia'),
      testId: 'perfil-sociodemografico-bloque-salud',
      filas: [
        {
          clave: 'estado_civil',
          etiqueta: t('perfilSociodemografico.estadoCivil'),
          valor: opcion('perfilSociodemografico.estadoCivil', perfil.estado_civil),
        },
        {
          clave: 'tipo_sangre',
          etiqueta: t('perfilSociodemografico.tipoSangre'),
          valor: opcion('perfilSociodemografico.tipoSangre', perfil.tipo_sangre),
        },
        {
          clave: 'contacto_emergencia',
          etiqueta: t('perfilSociodemografico.contactoEmergencia'),
          valor: contacto ? (
            <>
              {contacto}
              {perfil.contacto_emergencia_telefono && (
                <span className="font-mono"> · {perfil.contacto_emergencia_telefono}</span>
              )}
            </>
          ) : (
            VACIO
          ),
        },
        {
          clave: 'imc',
          etiqueta: t('perfilSociodemografico.imc'),
          valor: perfil.imc != null ? perfil.imc.toFixed(1) : VACIO,
        },
      ],
    },
  ]

  return <FichaDatos bloques={bloques} testId="perfil-sociodemografico-sealed-summary" />
}
